import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CapabilityTypes } from '@mono/proto-clients';
import { Badge, Button } from '@mono/ui-components';
import { AlertTriangle, Loader2, Play, ShieldCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCapabilityMatrix } from '../../../../hooks/useCapabilityMatrix';
const { ObligationStrength } = CapabilityTypes;
function scoreColor(score) {
    if (score >= 80) return 'text-green-700 bg-green-50';
    if (score >= 50) return 'text-yellow-700 bg-yellow-50';
    return 'text-red-700 bg-red-50';
}
function ScoreBadge({ score }) {
    return /*#__PURE__*/ _jsxs("span", {
        className: `inline-flex px-2 py-0.5 rounded text-xs font-medium ${scoreColor(score)}`,
        children: [
            Math.round(score),
            "%"
        ]
    });
}
/** Three-state status badge aligned with summary card tiers. */ function StatusBadge({ score }) {
    if (score >= 80) {
        return /*#__PURE__*/ _jsx(Badge, {
            variant: "default",
            className: "text-xs",
            children: "Covered"
        });
    }
    if (score >= 50) {
        return /*#__PURE__*/ _jsx(Badge, {
            variant: "outline",
            className: "text-xs text-yellow-700 border-yellow-300",
            children: "Partial"
        });
    }
    return /*#__PURE__*/ _jsx(Badge, {
        variant: "destructive",
        className: "text-xs",
        children: "Gap"
    });
}
export function CapabilityMatrixPage() {
    const { id } = useParams();
    const { gapAnalysisQuery, runMatchMutation, isReady } = useCapabilityMatrix(id ?? '');
    const data = gapAnalysisQuery.data;
    const summary = data?.gapSummary;
    const matches = data?.matches ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto flex h-full w-full max-w-7xl flex-col px-4 pt-4 sm:px-6 lg:px-8",
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "mb-4 flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-lg font-semibold text-slate-900",
                                children: "Capability Matrix"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-slate-500",
                                children: "Match your vendor library capabilities against this proposal's requirements."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        onClick: ()=>runMatchMutation.mutate(),
                        disabled: !isReady || runMatchMutation.isPending,
                        children: [
                            runMatchMutation.isPending ? /*#__PURE__*/ _jsx(Loader2, {
                                className: "w-4 h-4 animate-spin mr-2"
                            }) : /*#__PURE__*/ _jsx(Play, {
                                className: "w-4 h-4 mr-2"
                            }),
                            "Run Match"
                        ]
                    })
                ]
            }),
            summary && /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6",
                children: [
                    /*#__PURE__*/ _jsx(SummaryCard, {
                        label: "Total",
                        value: summary.totalRequirements
                    }),
                    /*#__PURE__*/ _jsx(SummaryCard, {
                        label: "Fully Matched",
                        value: summary.fullyMatched,
                        className: "text-green-700"
                    }),
                    /*#__PURE__*/ _jsx(SummaryCard, {
                        label: "Partial",
                        value: summary.partiallyMatched,
                        className: "text-yellow-700"
                    }),
                    /*#__PURE__*/ _jsx(SummaryCard, {
                        label: "Not Matched",
                        value: summary.notMatched,
                        className: "text-red-700"
                    }),
                    /*#__PURE__*/ _jsx(SummaryCard, {
                        label: "Critical Gaps",
                        value: summary.criticalGaps,
                        className: "text-red-700",
                        icon: summary.criticalGaps > 0 ? /*#__PURE__*/ _jsx(AlertTriangle, {
                            className: "w-4 h-4"
                        }) : undefined
                    })
                ]
            }),
            gapAnalysisQuery.isLoading && /*#__PURE__*/ _jsx("div", {
                className: "flex items-center justify-center py-12",
                children: /*#__PURE__*/ _jsx(Loader2, {
                    "data-testid": "matrix-loading-spinner",
                    className: "w-6 h-6 animate-spin text-slate-400"
                })
            }),
            gapAnalysisQuery.error && /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-red-500 py-4",
                children: "Failed to load capability matrix. Please try refreshing."
            }),
            !gapAnalysisQuery.isLoading && !gapAnalysisQuery.error && matches.length === 0 && /*#__PURE__*/ _jsxs("div", {
                "data-testid": "matrix-empty-state",
                className: "text-center py-12 border rounded-lg bg-slate-50",
                children: [
                    /*#__PURE__*/ _jsx(ShieldCheck, {
                        className: "w-10 h-10 mx-auto text-slate-300 mb-2"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-slate-500",
                        children: "No matching results yet."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-slate-400",
                        children: 'Click "Run Match" to score your capabilities against this proposal\'s requirements.'
                    })
                ]
            }),
            matches.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "border rounded-lg overflow-auto flex-1 min-h-0",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full text-sm",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-slate-50 border-b",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left px-4 py-2.5 font-medium text-slate-600",
                                        children: "Requirement"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left px-4 py-2.5 font-medium text-slate-600 w-24",
                                        children: "Obligation"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left px-4 py-2.5 font-medium text-slate-600 w-20",
                                        children: "Score"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "text-left px-4 py-2.5 font-medium text-slate-600 w-24",
                                        children: "Status"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            className: "divide-y",
                            children: matches.map((match)=>/*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": "matrix-row",
                                    className: "hover:bg-slate-50",
                                    children: [
                                        /*#__PURE__*/ _jsxs("td", {
                                            className: "px-4 py-3",
                                            children: [
                                                /*#__PURE__*/ _jsx("p", {
                                                    className: "text-slate-900 line-clamp-2",
                                                    children: match.requirementText
                                                }),
                                                (()=>{
                                                    const matched = (match.matchedCapabilities ?? []).filter((c)=>c.matchScore >= 50);
                                                    if (matched.length === 0) return null;
                                                    return /*#__PURE__*/ _jsx("div", {
                                                        className: "mt-1 flex flex-wrap gap-1",
                                                        children: matched.slice(0, 3).map((c)=>/*#__PURE__*/ _jsxs(Badge, {
                                                                variant: "secondary",
                                                                className: "text-xs",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(ScoreBadge, {
                                                                        score: c.matchScore
                                                                    }),
                                                                    ' ',
                                                                    (c.justification ?? '').slice(0, 60),
                                                                    (c.justification ?? '').length > 60 ? '...' : ''
                                                                ]
                                                            }, c.capabilityId))
                                                    });
                                                })()
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-4 py-3",
                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                variant: match.obligationStrength === ObligationStrength.MUST ? 'destructive' : 'outline',
                                                className: "text-xs",
                                                children: obligationLabel(match.obligationStrength)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-4 py-3",
                                            children: /*#__PURE__*/ _jsx(ScoreBadge, {
                                                score: match.overallMatchScore
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-4 py-3",
                                            children: /*#__PURE__*/ _jsx(StatusBadge, {
                                                score: match.overallMatchScore
                                            })
                                        })
                                    ]
                                }, match.requirementId))
                        })
                    ]
                })
            })
        ]
    });
}
function SummaryCard({ label, value, className = '', icon }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-lg border bg-white px-4 py-3",
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-slate-500",
                children: label
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: `text-2xl font-semibold ${className}`,
                children: [
                    icon && /*#__PURE__*/ _jsx("span", {
                        className: "inline-block mr-1 align-middle",
                        children: icon
                    }),
                    value
                ]
            })
        ]
    });
}
function obligationLabel(strength) {
    switch(strength){
        case ObligationStrength.MUST:
            return 'MUST';
        case ObligationStrength.SHOULD:
            return 'SHOULD';
        case ObligationStrength.MAY:
            return 'MAY';
        default:
            return '—';
    }
}
